import { FlowState } from '@de/providers/dynamicExperienceReducer';
import { getOptionByCustomField } from '@de/utils/getOptionByCustomField';
import {
  getOptionValueString,
  extractCustomFieldFromQueryParam,
} from '@de/utils/queryParamUtils';
import { DynamicOptions, Option, Question, Step } from '@de/types/types';
import { fetchDynamicOptions } from './fetchDynamicOptions';
import { StepType } from '@de/types/enums';

export type DynamicOptionsCache = Record<string, Option[]>;

interface PrefetchResult {
  stepId: string;
  options: Option[];
  success: true;
}

interface PrefetchError {
  stepId: string;
  error: Error;
  success: false;
}

type PrefetchOutcome = PrefetchResult | PrefetchError;

/** Minimal shape for dependency / cache-key logic (top-level steps or MULTI_QUESTION children). */
export type DynamicOptionsDependencySource = {
  id?: string;
  dynamicOptions?: Question['dynamicOptions'];
};

// Extracts the customField names that a step depends on from its queryParams config.
export const getDependencies = (
  step: DynamicOptionsDependencySource,
): string[] => {
  const queryParams = step.dynamicOptions?.queryParams;

  if (!queryParams || queryParams.length === 0) {
    return [];
  }

  return queryParams.map(
    (param) => extractCustomFieldFromQueryParam(param).customField,
  );
};

/**
 * Step ids whose flowState entries affect dynamic-options dependency keys for any
 * step with dynamicOptions (i.e. answers for customFields referenced in queryParams).
 * Used to scope prefetch debounce to relevant flow changes instead of every keystroke.
 */
export const getStepIdsAffectingDynamicOptionsPrefetch = (
  steps: Step[],
): string[] => {
  const neededFields = new Set<string>();
  for (const s of steps) {
    if (s.type === StepType.QUESTION) {
      const q = s as Question;
      if (q.dynamicOptions?.endpointUrl) {
        for (const f of getDependencies(q)) {
          neededFields.add(f);
        }
      }
    }
  }
  const ids = new Set<string>();
  for (const s of steps) {
    if (s.type === StepType.QUESTION && s.id) {
      const q = s as Question;
      if (q.customField && neededFields.has(q.customField)) {
        ids.add(s.id);
      }
    }
  }
  return Array.from(ids).sort();
};

// Checks if ALL dependencies for a step are satisfied in the current flowState.
export const areDependenciesSatisfied = (
  step: Step,
  flowState: FlowState,
  steps: Step[],
): boolean => {
  const dependencies = getDependencies(step);

  // No dependencies
  if (dependencies.length === 0) {
    return true;
  }

  // Check each dependency
  return dependencies.every((customField) => {
    const option = getOptionByCustomField(customField, steps, flowState);
    return option !== null && option !== undefined;
  });
};

/**
 * Determines if a step's dynamic options can be pre-fetched with current flowState.
 *
 * A step can be pre-fetched when
 * it is a QUESTION type step
 * and it has a dynamicOptions config with an endpointUrl
 * and all its dependencies (queryParams) are satisfied
 */
export const canPrefetchStep = (
  step: Step,
  flowState: FlowState,
  steps: Step[],
): boolean => {
  // Must be a question type
  if (step.type !== StepType.QUESTION) {
    return false;
  }

  const question = step as Question;

  // Must have dynamic options configured
  if (!question.dynamicOptions?.endpointUrl) {
    return false;
  }

  // All dependencies must be satisfied
  return areDependenciesSatisfied(step, flowState, steps);
};

// Pre-fetches dynamic options for a single step. Returns the result with success/failure status.
const prefetchSingleStep = async (
  step: Step,
  flowState: FlowState,
  steps: Step[],
): Promise<PrefetchOutcome> => {
  const question = step as Question;
  const stepId = step.id!;

  try {
    const options = await fetchDynamicOptions(
      question.dynamicOptions as DynamicOptions,
      flowState,
      steps,
    );

    return {
      stepId,
      options,
      success: true,
    };
  } catch (error) {
    console.warn(
      `[Prefetch] Failed to prefetch options for step ${stepId}:`,
      error,
    );
    return {
      stepId,
      error: error instanceof Error ? error : new Error(String(error)),
      success: false,
    };
  }
};

/**
 * Pre-fetches dynamic options for ALL eligible steps in the flow.
 *
 * This function
 * scans all steps for those with dynamic options,
 * checks if their dependencies are satisfied,
 * skips steps already in the cache,
 * and fetches all eligible steps in parallel.
 */
export const prefetchEligibleSteps = async (
  steps: Step[],
  flowState: FlowState,
  existingCache: DynamicOptionsCache = {},
): Promise<DynamicOptionsCache> => {
  // Find steps that can be pre-fetched and aren't already cached
  const stepsToFetch = steps.filter((step) => {
    // Skip if already cached
    if (step.id && existingCache[step.id]) {
      return false;
    }

    return canPrefetchStep(step, flowState, steps);
  });

  if (stepsToFetch.length === 0) {
    return existingCache;
  }

  // Fetch all eligible steps in parallel
  const results = await Promise.all(
    stepsToFetch.map((step) => prefetchSingleStep(step, flowState, steps)),
  );

  // Merge successful results into the cache
  const newCache = { ...existingCache };

  results.forEach((result) => {
    if (result.success) {
      newCache[result.stepId] = result.options;
    }
  });

  return newCache;
};
/**
 * Generates a cache key for a step based on its dependencies' values.
 * This is used to invalidate cached options when dependencies change.
 *
 */
export const generateCacheKey = (
  step: DynamicOptionsDependencySource,
  flowState: FlowState,
  steps: Step[],
): string => {
  const dependencies = getDependencies(step);
  const baseKey = step.id || 'unknown';

  if (dependencies.length === 0) {
    return baseKey;
  }

  const depValues = dependencies.map((customField) => {
    const option = getOptionByCustomField(customField, steps, flowState);
    const value = getOptionValueString(option);
    return `${customField}:${value ?? 'null'}`;
  });

  return `${baseKey}|${depValues.join('|')}`;
};
